import { FC, useEffect, useMemo, useRef } from "react";
import styles from "./CatalogFilter.module.scss";
import { useTranslation } from "next-i18next";
import { Button } from "@/lib/ui/components/Buttons";
import { SettingsIcon } from "@/lib/ui/icons";
import { DropDownMulti } from "@/lib/ui/components/Selects";
import { IValueLabel } from "@/lib/types/mainPage";
import { useCatalogFilter } from "@/lib/hooks/useCatalogFilter";
import { DropDownPrice } from "@/lib/ui/components/Selects/DropDownPrice";
import { CatalogQueryParam } from "@/lib/types/catalogPage";
import { modal } from "@/lib/ui/components/Modal";
import { CatalogFilterModal } from "../CatalogFilterModal";
import {
  beachDistanceOptions,
  bedroomsOptions,
  islandOptions,
  propertyTypeOptions,
} from "./constants";
import { CatalogMap } from "../CatalogMap";
import { MapPinIcon } from "@/lib/ui/icons/MapPinIcon";
import { useAppSelector } from "@/lib/store/app";
import { getFilteredProperties } from "@/lib/hooks/useFilteredProperties";
import cl from "classnames";
import { useCatalogActions } from "../../../../../store/slices/catalog";

export const CatalogFilterDropdownRow: FC<{ isFixed: boolean }> = ({ isFixed }) => {
  const {
    t,
  } = useTranslation("index");
  const properties = useAppSelector((state) => state.catalog.properties);
  const currency = useAppSelector((state) => state.settings.currency.value);
  const { query, setParam, resetParams } = useCatalogFilter();

  const selectedValuesHandler = (
    options: IValueLabel[],
    queryFilter: CatalogQueryParam,
  ) => {
    setParam({ key: queryFilter, value: options.map((o) => o.value) });
  };

  const propertyRef = useRef<any>(null);
  const islandRef = useRef<any>(null);
  const districtRef = useRef<any>(null);
  const bedroomsRef = useRef<any>(null);
  const beachRef = useRef<any>(null);
  const refs = [propertyRef, islandRef, districtRef, beachRef, bedroomsRef];

  const resetFilter = () => {
    refs.forEach((ref) => {
      if (!ref.current) return;
      ref.current.clearValue();
    });
    resetParams();
  };
  const translatedOptions = useMemo(() => {
    const translateAndDetectDisabled = (
      arr: IValueLabel[],
      catalogQuery: "propertyType" | "bedrooms" | "beachDistance" | "island",
    ) =>
      arr.map((item) => {
        const testedKey =
          catalogQuery === "island"
            ? `${String(item.value).toLowerCase()}District`
            : catalogQuery;
        const testedValue = catalogQuery === "island" ? "all" : item.value;
        const prefilteredResult = getFilteredProperties(
          properties,
          { ...query, [testedKey]: testedValue },
          currency,
        );

        return {
          ...item,
          label: catalogQuery === "island" ? t(`locations.${item.label}`) : t(item.label),
          isDisabled: catalogQuery !== "island" && prefilteredResult.length === 0,
        };
      });
    return {
      propertyType: translateAndDetectDisabled(propertyTypeOptions, "propertyType"),
      bedrooms: translateAndDetectDisabled(bedroomsOptions, "bedrooms"),
      beachDistance: translateAndDetectDisabled(beachDistanceOptions, "beachDistance"),
      island: translateAndDetectDisabled(islandOptions, "island"),
    };
  }, [currency, properties, query, t]);

  const getDefaultValue = useMemo(
    () => (type: keyof typeof translatedOptions) => {
      if (type === "island") {
        const result = ([] as { value: string }[])
          .concat(...(query["phuketDistrict"] ? [{ value: "PHUKET" }] : []))
          .concat(...(query["samuiDistrict"] ? [{ value: "SAMUI" }] : []));
        return result;
      } else {
        const q = query[type];
        if (!q) return [];
        return typeof q === "string" || typeof q === "number"
          ? [{ value: q }]
          : q.map((val) => ({ value: val }));
      }
    },
    [query],
  );

  const isEmptyFilters = useMemo(() => {
    return (
      Object.entries(query).length === 0 ||
      (Object.entries(query).length === 1 && !!query.page)
    );
  }, [query]);

  // if (!router.isReady) return null;
  return (
    <div className={styles.row}>
      <div className={styles.row__left}>
        <div className={styles.row__leftSection}>
          <DropDownMulti
            shouldHaveIndicator
            theme="dark"
            size={isFixed ? "sm" : undefined}
            reference={propertyRef}
            placeholder={t("propertyType")}
            selectedValuesHandler={(options) =>
              selectedValuesHandler(options, "propertyType")
            }
            value={getDefaultValue("propertyType")}
            options={translatedOptions.propertyType}
          />
          <DropDownMulti
            shouldHaveIndicator
            theme="dark"
            size={isFixed ? "sm" : undefined}
            reference={islandRef}
            placeholder={t("island")}
            selectedValuesHandler={(options) => {
              if (options.length === 1) {
                if (options[0].value === "PHUKET") {
                  setParam(undefined, {
                    phuketDistrict: "all",
                    samuiDistrict: undefined,
                  });
                } else {
                  setParam(undefined, {
                    phuketDistrict: undefined,
                    samuiDistrict: "all",
                  });
                }
              } else if (options.length === 2) {
                setParam(undefined, { phuketDistrict: "all", samuiDistrict: "all" });
              } else {
                setParam(undefined, {
                  phuketDistrict: undefined,
                  samuiDistrict: undefined,
                });
              }
            }}
            value={getDefaultValue("island")}
            options={translatedOptions.island}
          />
          <Button
            label={t("district")}
            theme="grayBorder"
            size="lg"
            as="button"
            className={cl(
              styles.row__toggle,
              ((query.phuketDistrict && query.phuketDistrict !== "all") ||
                (query.samuiDistrict && query.samuiDistrict !== "all")) &&
                styles["row__toggle--selected"],
              isFixed && styles["row__smallButton"],
            )}
            icon={<MapPinIcon />}
            onClick={() => {
              modal.open(<CatalogMap />, { isMapModal: true });
            }}
          />
        </div>

        <div className={styles.row__leftSection}>
          <DropDownPrice isFixed={isFixed} />
          <DropDownMulti
            shouldHaveIndicator
            theme="dark"
            size={isFixed ? "sm" : undefined}
            reference={bedroomsRef}
            placeholder={t("bedrooms")}
            selectedValuesHandler={(options) =>
              selectedValuesHandler(options, "bedrooms")
            }
            options={translatedOptions.bedrooms}
            value={getDefaultValue("bedrooms")}
          />
          <DropDownMulti
            shouldHaveIndicator
            theme="dark"
            size={isFixed ? "sm" : undefined}
            reference={beachRef}
            placeholder={t("catalog.beachDistance")}
            selectedValuesHandler={(options) =>
              selectedValuesHandler(options, "beachDistance")
            }
            value={getDefaultValue("beachDistance")}
            options={translatedOptions.beachDistance}
          />
        </div>
      </div>
      <div className={styles.row__right}>
        <Button
          label={t("reset")}
          theme="secondary"
          className={cl(isFixed && styles["row__smallButton"], styles.row__resetFilters)}
          size="lg"
          disabled={isEmptyFilters}
          as="button"
          onClick={resetFilter}
        />
        <Button
          label={t("allFilter")}
          theme="dark-invert"
          size="lg"
          as="button"
          onClick={() => modal.open(<CatalogFilterModal />, { isFilterModal: true })}
          className={cl(
            styles.row__button,
            isFixed && styles["row__smallButton"],
            !isEmptyFilters && styles["row__toggle--selected"],
          )}
          icon={<SettingsIcon />}
        />
      </div>
    </div>
  );
};
